import type { Dispositivo } from '../../tipos';

/**
 * Tipos e rótulos da tela de Inventário.
 *
 * Ficam fora dos componentes porque a lista, os cartões e a ficha falam do
 * mesmo dispositivo — duplicar a forma dele foi o que já deixou a tela ler
 * `kind` numa resposta que devolvia `intencao`.
 */

export interface Resumo {
  readonly total: number;
  readonly distribuicao: readonly {
    readonly versao: string;
    readonly total: number;
  }[];
  readonly sinais: {
    readonly semInventarioRecente: number;
    readonly semAgente: number;
    readonly nuncaInventariado: number;
  };
  readonly aptidaoNaoClassificados?: number;
  readonly cicloDeVida?: {
    readonly trocar: number;
    readonly trocar_ao_fim_da_garantia: number;
    readonly renovar_garantia: number;
    readonly manter: number;
    readonly sem_dados: number;
  };
  readonly aptidaoWin11?: {
    readonly ja_atende: number;
    readonly apto: number;
    readonly apto_com_ressalva: number;
    readonly inapto: number;
    readonly indeterminado: number;
  };
  readonly ultimaSincronizacao: { readonly concluidaEm: string | null } | null;
  readonly kaceConfigurado: boolean;
}

export interface ComViabilidade extends Dispositivo {
  readonly viabilidade?: string;
  readonly viabilidadeResumo?: string;
  readonly recomendacao?: string;
  readonly recomendacaoMotivo?: string;
  readonly diasDeGarantia?: number | null;
}

/**
 * O que fazer com a máquina — aptidão cruzada com garantia.
 *
 * O KACE tem os dois dados e não junta nenhum deles. O cruzamento é o que
 * transforma duas telas de consulta numa lista de compra com justificativa.
 */
export const CICLO: Record<string, { rotulo: string; tom: 'ok' | 'alerta' | 'erro' | 'neutro' }> = {
  trocar: { rotulo: 'Trocar', tom: 'erro' },
  trocar_ao_fim_da_garantia: {
    rotulo: 'Trocar ao fim da garantia',
    tom: 'alerta',
  },
  renovar_garantia: { rotulo: 'Renovar garantia', tom: 'alerta' },
  manter: { rotulo: 'Manter', tom: 'ok' },
  sem_dados: { rotulo: 'Sem dados', tom: 'neutro' },
};

export interface Pagina {
  readonly itens: readonly ComViabilidade[];
  readonly total: number;
  /** Filtro de aptidão é peneirado fora do banco — o total é do recorte. */
  readonly filtradoNaMemoria?: boolean;
}

/**
 * Aptidão para Windows 11.
 *
 * Quatro estados, não dois. "Sem dados para decidir" tem lugar próprio porque
 * máquina cujo TPM ninguém mediu **não é** máquina inapta — tratá-la como tal
 * excluiria do projeto de upgrade equipamento que talvez esteja pronto.
 */
export const APTIDAO: Record<string, { rotulo: string; tom: 'ok' | 'alerta' | 'erro' | 'neutro' }> =
  {
    ja_atende: { rotulo: 'Já no 11', tom: 'ok' },
    apto: { rotulo: 'Apto', tom: 'ok' },
    apto_com_ressalva: { rotulo: 'Apto com ressalva', tom: 'alerta' },
    inapto: { rotulo: 'Inapto', tom: 'erro' },
    indeterminado: { rotulo: 'Sem dados', tom: 'neutro' },
  };

/**
 * Filtro que chegou de um cartão do painel.
 *
 * Vem inteiro, não traduzido para os três filtros da tela — a maioria dos
 * cartões não caberia neles. Enquanto está ativo, os controles comuns ficam de
 * lado e a tarja explica de onde veio, com saída visível.
 */
export interface FiltroDeCartao {
  readonly titulo: string;
  readonly explicacao: string;
  readonly consulta: unknown;
  /**
   * A resposta numérica, quando a pergunta foi de **contagem**.
   *
   * 🔴 **Medido:** *"Quantas máquinas temos?"* devolvia `intencao: "contar"` e
   * `total: 25` — e esta tela lia só `kind` e `linhas` da resposta. O número
   * era descartado e trocado por uma listagem **sem filtro**, visualmente
   * idêntica à lista que já estava aberta.
   *
   * Quem perguntou esperou 99 segundos, viu a mesma tabela de antes e concluiu,
   * com razão, que nada tinha voltado. A resposta existia; ela só não era
   * desenhada em lugar nenhum.
   */
  readonly total?: number | null;
  /**
   * A frase em português, quando a rota a compôs.
   *
   * Preferida à dupla "número + explicação técnica": ver `consultas/redacao.ts`.
   */
  readonly frase?: string | null;
}

/** O que a rota de perguntar devolve, na parte que esta tela precisa ler. */
export interface RespostaDaPergunta {
  readonly consulta: { readonly intencao: string };
  readonly resultado?: {
    readonly kind?: string;
    /** O caminho compilado devolve `intencao`; o de software devolve `kind`. */
    readonly intencao?: string;
    readonly total?: number;
  };
}

/**
 * O número, quando a pergunta foi de contagem — e `null` quando não foi.
 *
 * 🔴 **Medido, com a aplicação rodando.** *"Quantas máquinas temos?"* devolveu
 * `intencao: "contar"` e `total: 25`, e esta tela lia apenas `kind` e `linhas`.
 * O 25 era descartado e substituído por uma listagem **sem filtro**, idêntica à
 * tabela que já estava aberta. Depois de 99 segundos de espera, a leitura
 * correta de quem perguntou foi "não voltou nada" — e o produto tinha a
 * resposta na mão.
 *
 * ⚠️ Só devolve número para pergunta de contagem. Numa pergunta de listagem o
 * `total` é o tamanho do recorte, e mostrá-lo em destaque ao lado da frase o
 * faria parecer a resposta a uma pergunta que ninguém fez.
 */
export function totalDaContagem(r: RespostaDaPergunta): number | null {
  const contou = r.consulta.intencao === 'contar' || r.resultado?.intencao === 'contar';
  if (!contou) return null;
  return typeof r.resultado?.total === 'number' ? r.resultado.total : null;
}

/**
 * A versão do Windows, em português.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 **A chave era `win7`. O domínio emite `win7_ou_anterior`.**
 *
 * Nenhuma máquina caía no rótulo: quem estivesse em Windows 7 aparecia na tela
 * com a chave crua do banco. É a mesma classe de erro que `vocabulario.ts` já
 * documenta para o filtro — a chave parecida que compila, roda e não casa com
 * nada.
 *
 * ⚠️ A lista canônica é `VALORES.versao_windows`, em
 * `domain/consulta/vocabulario.ts`. `tipos.test.ts` lê aquele arquivo e exige
 * um rótulo para cada valor — acrescentar uma versão lá e esquecer aqui passa a
 * quebrar o teste, em vez de aparecer na tela do cliente.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export const ROTULO_VERSAO: Record<string, string> = {
  win11: 'Windows 11',
  win10: 'Windows 10',
  win8: 'Windows 8',
  win7_ou_anterior: 'Windows 7 ou anterior',
  /**
   * Não é "sem dado": é máquina que não roda Windows.
   *
   * O produto separa três estados, e `n/a` é o terceiro — conhecido e sem
   * versão de Windows aplicável. Mostrar "n/a" cru deixava o leitor decidir
   * sozinho se era Linux ou falha de coleta.
   */
  'n/a': 'Não é Windows',
};
